import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import {
  PhoneCall,
  Target,
  Handshake,
  Info,
  AlertTriangle,
  Gauge,
} from 'lucide-react';
import { backofficeApi, formatNumber, formatDate, extractErrorMessage } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import { theme } from '@kardume/theme';
import type {
  ComboItem,
  InterestCohortKind,
  MerchantOpportunityDetailDto,
  MerchantOpportunityOutcome,
  OpportunityQuery,
  Tone,
} from '@kardume/types';
import {
  PageHeader,
  StatCard,
  Card,
  CardHeader,
  CardBody,
  Table,
  TableContainer,
  Badge,
  Button,
  Select,
  SearchableComboBox,
  Drawer,
  Pagination,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from '../components/DataState';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1.25rem;

  /* O combobox põe a borda no wrapper e o select põe nele mesmo; sem isto a
     linha de filtros fica escalonada. */
  select {
    box-sizing: border-box;
    height: 42px;
  }
`;

const FilterField = styled.div`
  flex: 1 1 200px;
  min-width: 180px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  margin-bottom: 1.25rem;
`;

const Muted = styled.span`
  color: ${(p) => p.theme.textMuted};
`;

const Numeric = styled.td`
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const NumericHead = styled.th`
  text-align: right;
`;

const ClickableRow = styled.tr`
  cursor: pointer;

  &:hover {
    background: ${(p) => p.theme.primaryLight};
  }
`;

const ScoreValue = styled.strong<{ $tone: Tone }>`
  font-size: 1.05rem;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$tone === 'success' ? p.theme.success : p.theme.text)};
`;

const Note = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 1rem 0 0;
  font-size: 0.8rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textMuted};

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const Warning = styled(Note)`
  color: ${(p) => p.theme.warning};
`;

const DrawerSection = styled.section`
  margin-bottom: 1.5rem;

  h4 {
    margin: 0 0 0.5rem;
    font-size: 0.9rem;
  }
`;

const Facts = styled.dl`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.35rem 1rem;
  margin: 0;
  font-size: 0.85rem;

  dt {
    color: ${(p) => p.theme.textMuted};
  }

  dd {
    margin: 0;
    font-variant-numeric: tabular-nums;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const PERIOD_OPTIONS = [
  { value: '', label: 'Janela do job (padrão)' },
  { value: '7', label: 'Janela de 7 dias' },
  { value: '30', label: 'Janela de 30 dias' },
  { value: '90', label: 'Janela de 90 dias' },
];

const MIN_SCORE_OPTIONS = [
  { value: '0', label: 'Qualquer score' },
  { value: '50', label: 'Score 50+ (a partir da mediana)' },
  { value: '70', label: 'Score 70+' },
  { value: '85', label: 'Score 85+ (só o topo)' },
];

const OUTCOME_LABEL: Record<MerchantOpportunityOutcome, string> = {
  PENDING: 'Não abordada',
  CONTACTED: 'Contactada',
  NOT_INTERESTED: 'Sem interesse',
  CONVERTED: 'Virou parceira',
  DO_NOT_CONTACT: 'Não abordar',
};

const OUTCOME_TONE: Record<MerchantOpportunityOutcome, Tone> = {
  PENDING: 'secondary',
  CONTACTED: 'info',
  NOT_INTERESTED: 'warning',
  CONVERTED: 'success',
  DO_NOT_CONTACT: 'danger',
};

const COHORT_LABEL: Record<InterestCohortKind, string> = {
  CITY_CATEGORY: 'mesma cidade e categoria',
  CITY: 'mesma cidade',
  PLATFORM: 'guia inteiro',
};

const EVENT_LABEL: Record<string, string> = {
  PROFILE_VIEW: 'Abriu a página',
  WHATSAPP_CLICK: 'Clicou no WhatsApp',
  PHONE_CLICK: 'Clicou no telefone',
  DIRECTIONS_CLICK: 'Pediu rota',
  WEBSITE_CLICK: 'Abriu o site',
  INSTAGRAM_CLICK: 'Abriu o Instagram',
  SHARE: 'Compartilhou',
  FAVORITE: 'Favoritou',
  WANT_TO_VISIT: 'Quero conhecer',
};

/** Uma casa decimal. O score é inteiro; as taxas não, e três casas não cabem na frase. */
const rate = (value: number) => value.toFixed(1).replace('.', ',');

/**
 * Oportunidades comerciais de empresas não parceiras (card 238, KU-29).
 *
 * ## Que pergunta esta tela responde
 *
 * O #258 mostra onde há procura no guia. Esta responde a pergunta seguinte, que
 * é a que o comercial faz de manhã: **para quem eu ligo hoje?** A diferença não
 * é de recorte, é de ordenação - aqui o primeiro lugar não é quem tem mais
 * eventos, é quem está mais acima do normal da própria coorte.
 *
 * ## Por que o score aparece sempre com a conta ao lado
 *
 * Score opaco vira desconfiança interna e para de ser usado. Alguém vai
 * perguntar por que a padaria X está em terceiro, e a resposta precisa estar na
 * própria tela: quais eventos, com que peso, contra qual grupo de empresas e
 * com que mediana. É o que o drawer mostra, e é por isso que o core grava cada
 * cálculo como fotografia em vez de recalculá-lo a cada leitura.
 *
 * ## O aviso de amostra pequena não é enfeite
 *
 * Score alto sobre coorte de três empresas é a forma mais fácil de o relatório
 * mentir com aparência de rigor. Quando a comparação é frouxa a tela diz isso
 * na linha, e não só no detalhe - porque é na lista que a decisão de ligar é
 * tomada.
 *
 * ## Nada aqui é dado pessoal
 *
 * Todo número vem do agregado do #230, que não guarda IP, conta nem telefone de
 * visitante. Os contatos exibidos são os DA EMPRESA, os mesmos que a página
 * pública dela já mostra.
 */
const OpportunitiesPage: React.FC = () => {
  const { showNotification } = useNotification();

  const [city, setCity] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [minScore, setMinScore] = useState('0');
  const [windowDays, setWindowDays] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [selected, setSelected] = useState<MerchantOpportunityDetailDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const filters: OpportunityQuery = useMemo(
    () => ({
      ...(city ? { city } : {}),
      ...(categoryId ? { categoryId } : {}),
      ...(Number(minScore) > 0 ? { minScore: Number(minScore) } : {}),
      ...(windowDays ? { windowDays: Number(windowDays) } : {}),
    }),
    [city, categoryId, minScore, windowDays]
  );

  const summary = useAsync(() => backofficeApi.getOpportunitySummary(filters), [filters]);
  const funnel = useAsync(() => backofficeApi.getOpportunityFunnel(), []);
  const ranking = useAsync(
    () => backofficeApi.listOpportunities({ ...filters, page, size: pageSize }),
    [filters, page, pageSize]
  );

  const localities = useAsync(() => backofficeApi.listLocalities({ size: 200, active: true }), []);
  const categories = useAsync(() => backofficeApi.listCategories({ size: 200 }), []);

  /**
   * Qualquer troca de filtro volta para a primeira página.
   *
   * Sem isto, filtrar estando na página 4 mostra uma lista vazia que parece
   * "não há oportunidade" - e o operador conclui o oposto do que o dado diz.
   */
  const changeFilter = (apply: () => void) => {
    apply();
    setPage(0);
  };

  /**
   * O filtro de cidade é por NOME, e não por id de localidade.
   *
   * A fotografia do score guarda a cidade que a empresa tinha no momento dos
   * eventos, como texto - é o que preserva o recorte histórico (V71). Casar por
   * id exigiria resolver a localidade de hoje, que é justamente o que apagaria
   * o passado de quem mudou de cidade.
   */
  const cityItems: ComboItem[] = useMemo(
    () => (localities.data?.content ?? []).map((l) => ({ id: l.name, label: `${l.name}/${l.state}` })),
    [localities.data]
  );

  const categoryItems: ComboItem[] = useMemo(
    () => (categories.data?.content ?? []).map((c) => ({ id: c.id, label: c.name })),
    [categories.data]
  );

  const openDetail = (merchantId: string) => {
    setDetailLoading(true);
    backofficeApi
      .getOpportunity(merchantId, windowDays ? Number(windowDays) : undefined)
      .then(setSelected)
      .catch((err) => showNotification(extractErrorMessage(err, 'Não foi possível abrir o detalhe.'), 'error'))
      .finally(() => setDetailLoading(false));
  };

  const refreshAll = () => {
    ranking.reload();
    summary.reload();
    funnel.reload();
  };

  const generateLead = () => {
    if (!selected) return;
    setSaving(true);
    backofficeApi
      .generateOpportunityLead(selected.merchantId, windowDays ? Number(windowDays) : undefined)
      .then((updated) => {
        setSelected(updated);
        showNotification('Lead criado no board comercial com o número do período.', 'success');
        refreshAll();
      })
      .catch((err) => showNotification(extractErrorMessage(err, 'Não foi possível gerar o lead.'), 'error'))
      .finally(() => setSaving(false));
  };

  const markOutcome = (outcome: MerchantOpportunityOutcome) => {
    if (!selected) return;
    setSaving(true);
    backofficeApi
      .markOpportunityOutcome(selected.merchantId, outcome)
      .then((updated) => {
        setSelected(updated);
        showNotification(`Marcada como "${OUTCOME_LABEL[outcome]}".`, 'success');
        refreshAll();
      })
      .catch((err) => showNotification(extractErrorMessage(err, 'Não foi possível marcar o resultado.'), 'error'))
      .finally(() => setSaving(false));
  };

  const rows = ranking.data?.content ?? [];
  const totals = summary.data;
  const conversion = funnel.data;

  return (
    <>
      <PageHeader
        title="Oportunidades comerciais"
        subtitle={
          totals?.windowTo
            ? `Empresas não parceiras com procura real, na janela de ${totals.windowDays} dias encerrada em ${formatDate(totals.windowTo)}.`
            : 'Empresas não parceiras com procura real - para quem vale a pena ligar hoje.'
        }
      />

      <Toolbar>
        <FilterField>
          <SearchableComboBox
            label="Cidade"
            items={cityItems}
            valueId={city}
            onChange={(id) => changeFilter(() => setCity(id))}
            placeholder="Todas as cidades"
            loading={localities.loading}
            noResultsText="Nenhuma cidade cadastrada"
          />
        </FilterField>
        <FilterField>
          <SearchableComboBox
            label="Categoria"
            items={categoryItems}
            valueId={categoryId}
            onChange={(id) => changeFilter(() => setCategoryId(id))}
            placeholder="Todas as categorias"
            loading={categories.loading}
            noResultsText="Nenhuma categoria cadastrada"
          />
        </FilterField>
        <FilterField>
          <Select
            label="Score mínimo"
            options={MIN_SCORE_OPTIONS}
            value={minScore}
            onChange={(e) => changeFilter(() => setMinScore(e.target.value))}
          />
        </FilterField>
        <FilterField>
          <Select
            label="Janela"
            options={PERIOD_OPTIONS}
            value={windowDays}
            onChange={(e) => changeFilter(() => setWindowDays(e.target.value))}
          />
        </FilterField>
      </Toolbar>

      {/* Resumo, funil e ranking carregam separados: paginar a lista não pode
          recarregar os cartões, e um erro num bloco não pode apagar o outro. */}
      <DataState loading={summary.loading} error={summary.error} onRetry={summary.reload}>
        {totals && (
          <Grid>
            <StatCard
              label={`Acima do limiar (${totals.leadThreshold})`}
              value={formatNumber(totals.aboveThreshold)}
              icon={<Target size={18} />}
              iconColor={theme.accent}
            />
            <StatCard
              label="Com score calculado"
              value={formatNumber(totals.scored)}
              icon={<Gauge size={18} />}
              iconColor={theme.primary}
            />
            <StatCard
              label="Já contactadas"
              value={formatNumber(conversion?.contacted ?? 0)}
              icon={<PhoneCall size={18} />}
              iconColor={theme.info}
            />
            <StatCard
              label="Viraram parceiras"
              value={formatNumber(conversion?.converted ?? 0)}
              icon={<Handshake size={18} />}
              iconColor={theme.success}
            />
          </Grid>
        )}
      </DataState>

      <DataState
        loading={ranking.loading}
        error={ranking.error}
        onRetry={ranking.reload}
        isEmpty={rows.length === 0}
        emptyTitle="Nenhuma oportunidade neste recorte"
        emptyDescription="Troque o filtro, ou espere a próxima passada do cálculo. Parceiras ativas e empresas marcadas como 'não abordar' nunca aparecem aqui."
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <NumericHead>Score</NumericHead>
                <th>Empresa</th>
                <th>Cidade</th>
                <th>Categoria</th>
                <NumericHead>Interesse</NumericHead>
                <th>Comparada com</th>
                <th>Situação</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <ClickableRow key={row.merchantId} onClick={() => openDetail(row.merchantId)}>
                  <Numeric>
                    <ScoreValue $tone={row.score >= (totals?.leadThreshold ?? 70) ? 'success' : 'secondary'}>
                      {row.score}
                    </ScoreValue>
                  </Numeric>
                  <td>
                    <strong>{row.merchantName}</strong>
                  </td>
                  <td>{row.city ?? <Muted>-</Muted>}</td>
                  <td>{row.categoryName ?? <Muted>-</Muted>}</td>
                  <Numeric>{formatNumber(row.rawEvents)}</Numeric>
                  <td>
                    <Muted>
                      {row.cohortSize} empresa(s), {COHORT_LABEL[row.cohortKind]}
                    </Muted>{' '}
                    {/* O aviso vai na LINHA, e não só no detalhe: é aqui que a
                        decisão de ligar é tomada, e um score alto com coorte de
                        três empresas não pode parecer igual a um com coorte cheia. */}
                    {row.smallSample && <Badge tone="warning">amostra pequena</Badge>}
                  </td>
                  <td>
                    <Badge tone={OUTCOME_TONE[row.outcome]}>{OUTCOME_LABEL[row.outcome]}</Badge>
                  </td>
                </ClickableRow>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      {!ranking.error && (
        <Pagination
          currentPage={page + 1}
          totalPages={ranking.data?.totalPages ?? 0}
          totalItems={ranking.data?.totalElements ?? 0}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          disabled={ranking.loading}
          onPageChange={(next) => setPage(next - 1)}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
        />
      )}

      {conversion && conversion.detected > 0 && (
        <Card style={{ marginTop: '1.25rem' }}>
          <CardHeader>
            <h3>Conversão da prospecção</h3>
          </CardHeader>
          <CardBody>
            <Facts>
              <dt>Oportunidades detectadas</dt>
              <dd>{formatNumber(conversion.detected)}</dd>
              <dt>Contactadas</dt>
              <dd>{formatNumber(conversion.contacted)}</dd>
              <dt>Viraram parceiras</dt>
              <dd>{formatNumber(conversion.converted)}</dd>
              <dt>Tempo médio até o primeiro contato</dt>
              <dd>
                {/* Nulo e não zero: zero se leria como "atendemos na hora", que
                    é o oposto de "não atendemos ninguém ainda". */}
                {conversion.averageHoursToFirstContact === null
                  ? '- ninguém foi contactado ainda'
                  : `${rate(conversion.averageHoursToFirstContact)} h`}
              </dd>
            </Facts>
            {conversion.byScoreBand.length > 0 && (
              <Note>
                <Info size={15} />
                <span>
                  Acerto por faixa de score:{' '}
                  {conversion.byScoreBand
                    .map((b) => `${b.band} → ${b.converted}/${b.detected}`)
                    .join(' · ')}
                  . É este recorte que diz <strong>onde</strong> o score erra: se a faixa alta
                  converte igual à baixa, o limiar está alto demais e há ligação boa ficando na mesa.
                </span>
              </Note>
            )}
          </CardBody>
        </Card>
      )}

      <Drawer
        isOpen={selected !== null}
        onClose={() => setSelected(null)}
        title={selected?.merchantName ?? 'Oportunidade'}
        width="620px"
      >
        {selected && (
          <>
            {selected.smallSample && (
              <Warning>
                <AlertTriangle size={15} />
                <span>
                  A coorte tem apenas {selected.cohortSize} empresa(s) medida(s). A comparação é
                  frouxa e o score vale menos do que parece - confira o número bruto antes de
                  priorizar por ele.
                </span>
              </Warning>
            )}

            <DrawerSection>
              <h4>De onde vem o score {selected.score}</h4>
              <TableContainer>
                <Table>
                  <thead>
                    <tr>
                      <th>Evento</th>
                      <NumericHead>Quantos</NumericHead>
                      <NumericHead>Peso</NumericHead>
                      <NumericHead>Contribuição</NumericHead>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.components.map((c) => (
                      <tr key={c.eventType}>
                        <td>{EVENT_LABEL[c.eventType] ?? c.eventType}</td>
                        <Numeric>{formatNumber(c.events)}</Numeric>
                        <Numeric>{rate(c.weight)}</Numeric>
                        <Numeric>
                          <strong>{formatNumber(c.contribution)}</strong>
                        </Numeric>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan={3}>
                        <strong>Total de pontos ponderados</strong>
                      </td>
                      <Numeric>
                        <strong>{formatNumber(selected.weightedEvents)}</strong>
                      </Numeric>
                    </tr>
                  </tbody>
                </Table>
              </TableContainer>
            </DrawerSection>

            <DrawerSection>
              <h4>Como esses pontos viraram {selected.score}</h4>
              <Facts>
                <dt>Janela</dt>
                <dd>
                  {formatDate(selected.windowFrom)} a {formatDate(selected.windowTo)} (
                  {selected.windowDays} dias)
                </dd>
                <dt>Pontos por dia observados</dt>
                <dd>{rate(selected.observedDailyRate)}</dd>
                <dt>Mais {selected.priorDays} dias na mediana da coorte</dt>
                <dd>{rate(selected.dailyRate)} por dia</dd>
                <dt>Mediana da coorte ({COHORT_LABEL[selected.cohortKind]})</dt>
                <dd>
                  {rate(selected.cohortMedian)} por dia, sobre {selected.cohortSize} empresa(s)
                </dd>
                <dt>Configuração usada</dt>
                <dd>versão {selected.settingVersion}</dd>
              </Facts>
              <Note>
                <Info size={15} />
                <span>
                  A escala é relativa à coorte: <strong>50 é exatamente a mediana</strong> do grupo,
                  67 é o dobro dela e 75 é o triplo. Os {selected.priorDays} dias somados na mediana
                  existem para que pouca medição não vire posição alta - é o que impede a loja de
                  três eventos de aparecer em primeiro numa cidade parada.
                </span>
              </Note>
            </DrawerSection>

            {selected.history.length > 1 && (
              <DrawerSection>
                <h4>Evolução</h4>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={selected.history.map((p) => ({ dia: formatDate(p.windowTo), Score: p.score }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                    <XAxis dataKey="dia" tick={{ fontSize: 11, fill: theme.textMuted }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: theme.textMuted }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="Score" stroke={theme.primary} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </DrawerSection>
            )}

            <DrawerSection>
              <h4>Contato da empresa</h4>
              <Facts>
                <dt>WhatsApp</dt>
                <dd>{selected.contact.whatsapp ?? '-'}</dd>
                <dt>Telefone</dt>
                <dd>{selected.contact.phone ?? '-'}</dd>
                <dt>E-mail</dt>
                <dd>{selected.contact.email ?? '-'}</dd>
                <dt>Responsável</dt>
                <dd>{selected.contact.responsibleName ?? '-'}</dd>
                <dt>Endereço</dt>
                <dd>{selected.contact.address ?? '-'}</dd>
              </Facts>
              <Note>
                <Info size={15} />
                <span>
                  São os dados públicos da página da empresa no guia. Nenhum número desta tela
                  identifica quem visitou: a coleta não guarda IP, conta nem telefone de visitante.
                </span>
              </Note>
            </DrawerSection>

            <DrawerSection>
              <h4>O que fazer</h4>
              <Actions>
                <Button variant="primary" onClick={generateLead} disabled={saving || detailLoading}>
                  {selected.leadId ? 'Atualizar lead com o número de agora' : 'Gerar lead'}
                </Button>
                <Button variant="outline" onClick={() => markOutcome('CONTACTED')} disabled={saving}>
                  Contactada
                </Button>
                <Button variant="outline" onClick={() => markOutcome('NOT_INTERESTED')} disabled={saving}>
                  Sem interesse
                </Button>
                <Button variant="outline" onClick={() => markOutcome('CONVERTED')} disabled={saving}>
                  Virou parceira
                </Button>
                <Button variant="danger" onClick={() => markOutcome('DO_NOT_CONTACT')} disabled={saving}>
                  Não abordar
                </Button>
              </Actions>
              <Note>
                <Info size={15} />
                <span>
                  “Virou parceira” fecha o funil comercial e <strong>não</strong> muda o cadastro da
                  empresa - a parceria continua sendo escrita só na tela de Lojistas, com motivo e
                  histórico. “Não abordar” tira a empresa do ranking para sempre, e não é a mesma
                  coisa que “sem interesse”: este é um não de hoje, aquele é um pedido para ser
                  deixada em paz.
                </span>
              </Note>
            </DrawerSection>
          </>
        )}
      </Drawer>

      <Note>
        <Info size={15} />
        <span>
          O score compara cada empresa com a <strong>mediana da coorte dela</strong> - mesma cidade e
          categoria -, e não com o guia inteiro. É o que impede o ranking de virar a lista das
          categorias grandes: restaurante tem mais movimento que pet shop em qualquer semana do ano,
          e isso não é informação comercial nenhuma. Os eventos contados são só os ocorridos{' '}
          <strong>enquanto a empresa não era parceira</strong>.
        </span>
      </Note>
    </>
  );
};

export default OpportunitiesPage;
